/**
 * Zod boundary for the pure gang-sheet nesting engine (plan § 3, § 9).
 * Inches are the human-facing print geometry; the engine converts to px at `dpi`.
 * No `z.any`/`z.unknown` — placement + geometry are fully typed.
 */
import { z } from 'zod';
/**
 * Roll + sheet configuration (Decision 1). Every field is defaulted so a caller can
 * pass a partial spec (or none) and the engine fills the owner-locked defaults:
 * 22" roll, 0.375" gutter, 300 DPI, 120" default sheet length (240" hard ceiling).
 * `encoder` widens to `['png','tiff','pdf']` later with zero packing change.
 */
export const GangSheetSpecSchema = z.object({
    rollWidthIn: z.number().positive().max(120).default(22),
    maxLengthIn: z.number().positive().max(240).default(120),
    gutterIn: z.number().nonnegative().max(5).default(0.375),
    edgeMarginIn: z.number().nonnegative().max(5).default(0),
    dpi: z.number().int().positive().max(1200).default(300),
    allowRotation: z.boolean().default(true),
    encoder: z.enum(['png']).default('png'),
});
/**
 * Pure oversized check — NO packer dependency, so callers that must not bundle
 * `maxrects-packer` (the rest-api handler) can block oversized designs at batch creation,
 * the same way unmapped SKUs are blocked (§ 9). A piece fits if EITHER orientation stays
 * within the usable box (width across the roll, height along the sheet); oversized only
 * when neither does. Shared with `nest()` so the geometry rule has one definition.
 */
export function checkOversize(widthIn, heightIn, spec) {
    const { dpi } = spec;
    const rollWidthPx = Math.round(spec.rollWidthIn * dpi);
    const maxLengthPx = Math.round(spec.maxLengthIn * dpi);
    const edgeMarginPx = Math.round(spec.edgeMarginIn * dpi);
    const usableWidthPx = rollWidthPx - 2 * edgeMarginPx;
    const usableLengthPx = maxLengthPx - 2 * edgeMarginPx;
    const widthPx = Math.round(widthIn * dpi);
    const heightPx = Math.round(heightIn * dpi);
    const fitsUnrotated = widthPx <= usableWidthPx && heightPx <= usableLengthPx;
    const fitsRotated = spec.allowRotation && heightPx <= usableWidthPx && widthPx <= usableLengthPx;
    if (fitsUnrotated || fitsRotated)
        return { oversized: false, widthPx, heightPx };
    const narrowest = spec.allowRotation ? Math.min(widthPx, heightPx) : widthPx;
    const reason = narrowest > usableWidthPx
        ? `Design ${widthPx}×${heightPx}px exceeds the ${usableWidthPx}px usable roll width even rotated.`
        : `Design ${widthPx}×${heightPx}px exceeds the ${usableLengthPx}px max sheet length.`;
    return { oversized: true, reason, widthPx, heightPx };
}
/**
 * Largest sheet canvas (width×height, px) the compositor renders at its 3008 MB Lambda memory. The
 * per-sheet base buffer is width×height×4 bytes and sharp holds ~2 rasters + working copies, so peak
 * memory is dominated by ONE sheet's canvas (sheets render one at a time). CALIBRATED from two prod
 * runs (2026-07-09): 145 MP → 1461 MB and 253 MP → 1950 MB peak, i.e. ≈ 800 MB FIXED + ~4.5 MB per
 * megapixel (an earlier ~10 MB/MP guess wrongly folded the fixed overhead into the slope). At this
 * 294 MP cap a full sheet models to ~2.1 GB, ~29% under the 3008 MB cap (34" roll → 96"). The
 * ~428 MP sheet that OOM'd did so under the OLD accumulate-every-sheet code (pre-#67); with
 * one-sheet-at-a-time the modelled OOM ceiling is ~(3008−800)/4.5 ≈ 490 MP, so 294 MP keeps a real
 * margin. DEPLOYMENT limit, not physical: raise it if the 3008 MB cap is lifted (AWS case) or the
 * encoder is tiled. Safety net if a denser-than-measured sheet re-OOMs: the worker is hard-killed →
 * the stuck-batch sweeper marks it FAILED, and the operator can set a shorter per-batch length in
 * the Build dialog (bounded by memorySafeMaxLengthIn below).
 */
export const MAX_SHEET_CANVAS_PX = 294_000_000;
/**
 * Longest sheet (whole inches) whose canvas stays within {@link MAX_SHEET_CANVAS_PX} for a roll of
 * `rollWidthIn` at `dpi`. Callers cap `spec.maxLengthIn` to this at build time: the nester then
 * spills a longer job to more, shorter sheets — same total film, just more cuts — instead of
 * building one canvas that OOMs. e.g. a 34" roll at 300 DPI → 96"; a 22" roll → 148".
 */
export function memorySafeMaxLengthIn(rollWidthIn, dpi) {
    return Math.max(1, Math.floor(MAX_SHEET_CANVAS_PX / (rollWidthIn * dpi * dpi)));
}
/**
 * Clamp a spec's `maxLengthIn` to {@link memorySafeMaxLengthIn} for its roll width, returning the
 * spec unchanged when already within budget. Apply this everywhere a spec reaches {@link nest} —
 * at build AND in the compositor — so EVERY path (build, retry-composite, re-enqueue) nests a
 * renderable sheet: a too-long job (incl. a legacy pre-cap batch on retry) spills to more, shorter
 * sheets instead of building a canvas that OOMs the compositor.
 */
export function clampToMemorySafeLength(spec) {
    const safe = memorySafeMaxLengthIn(spec.rollWidthIn, spec.dpi);
    return spec.maxLengthIn > safe ? { ...spec, maxLengthIn: safe } : spec;
}
/**
 * Identity of the order line a placed copy belongs to. Round-trips onto every
 * placement so the compositor knows which design PNG to fetch and the batch item
 * rows can be written back. `variantId` is nullable (deleted/custom lines, § 7).
 */
export const NestItemRefSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    variantId: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
});
/**
 * One printable design instance to nest, with a copy count. `widthIn`/`heightIn`
 * are the TRUE print size (never scaled/mirrored — the packer only translates +
 * optionally rotates 90°). The engine expands `copies` into individual placements.
 */
export const NestPieceSchema = z.object({
    designId: z.string().min(1),
    placementId: z.string().min(1),
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    copies: z.number().int().positive(),
    ref: NestItemRefSchema,
});
/** Pure engine input: the pieces to nest + the sheet spec. */
export const NestInputSchema = z.object({
    pieces: z.array(NestPieceSchema),
    spec: GangSheetSpecSchema.default(() => GangSheetSpecSchema.parse({})),
});
/**
 * A single placed copy on a sheet, in px. `widthPx`/`heightPx` are the placed
 * (post-rotation) footprint; when `rot` is true the compositor rotates the source
 * design 90° before compositing so the source's dims match this box.
 */
export const SheetPlacementSchema = z.object({
    designId: z.string(),
    placementId: z.string(),
    copyIndex: z.number().int().nonnegative(),
    ref: NestItemRefSchema,
    xPx: z.number().int().nonnegative(),
    yPx: z.number().int().nonnegative(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    rot: z.boolean(),
});
/**
 * One physical gang sheet (one packer bin). `widthPx` is pinned to the roll width,
 * NOT the shrink-wrapped `bin.width` (`smart:true` narrows it — § 9). `heightPx` is
 * the used length (max placement bottom + edge margin), clamped to the length cap.
 */
export const NestSheetSchema = z.object({
    sheetIndex: z.number().int().nonnegative(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    placements: z.array(SheetPlacementSchema).min(1),
});
/** A design that cannot fit the roll even rotated (or exceeds the length cap) — a hard batch blocker (§ 9). */
export const OversizedPieceSchema = z.object({
    designId: z.string(),
    placementId: z.string(),
    ref: NestItemRefSchema,
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    reason: z.string(),
});
/** Pure engine output: the sheets, any oversized (blocked) pieces, and derived px constants. */
export const NestResultSchema = z.object({
    sheets: z.array(NestSheetSchema),
    oversized: z.array(OversizedPieceSchema),
    dpi: z.number().int().positive(),
    rollWidthPx: z.number().int().positive(),
    gutterPx: z.number().int().nonnegative(),
    edgeMarginPx: z.number().int().nonnegative(),
    /** Total placed copies across all sheets (excludes oversized). */
    placedCount: z.number().int().nonnegative(),
    /** Film density: placed design area ÷ total sheet area, 0..1. */
    densityPct: z.number().min(0).max(1),
});
//# sourceMappingURL=schemas.js.map